import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Grid, Card, CardContent, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Skeleton } from "@mui/material";
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import "../styles/invoice.scss";
import { globalGetService } from "../../../utils/globalApiServices";
import { getFleetInvCardData } from "../apiService";
import { checkApiStatus, getGlobalCookie } from "../../../utils";
import moment from "moment";
import { dateMonthYearDateFormat } from "../../../constants";

const formatAmount = (currency, amount) => {
  if (!amount) return "--";
  try {
    return new Intl.NumberFormat("en-US", { style: "decimal", currency, maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount}`;
  }
};

const getCurrencyValues = (amountObj={}) => {
  
  return Object.entries(amountObj)
    .filter(([, amount]) => Number(amount) > 0)
    .map(([label, amount]) => ({ label, amount: formatAmount(label, amount), }));
};

const getCards = (cardData) => {
  const lessorAccess = JSON.parse(getGlobalCookie('lessorAccess') || 'null');
  if (!cardData) return [];
  const invoiceTypeCount = cardData?.invoice_count?.invoice_type_count || {};
  const overdueTypeCount = cardData?.total_overdue_invoice_count?.invoice_type_count || {};

  return [
    {
      title: "Total Invoices",
      subtitle: cardData?.invoice_count?.total != null ? new Intl.NumberFormat("en-US").format(cardData?.invoice_count?.total) : "0",
      subtitleContent: `${cardData?.current_date && moment(cardData?.current_date).format(dateMonthYearDateFormat)}`,
      values: [],
      color: { main: "#6d40c5", contnet: "#94a3b8", amountcolor: "#6d40c5" },
    },
    {
      title: "Total Invoiced",
      subtitleContent: "",
      values: getCurrencyValues(cardData?.total_invoice_amount),
      color: { main: "#2e7d32", contnet: "#94a3b8", amountcolor: "#0e172a" },
    },
    {
      title: "Total Paid",
      subtitleContent: "",
      values: getCurrencyValues(cardData?.total_paid_amount),
      color: { main: "#0d9488", contnet: "#94a3b8", amountcolor: "#16a44a" },
    },
    {
      title: "Outstanding",
      subtitleContent: "Sent · Awaiting payment",
      values: getCurrencyValues(cardData?.total_outstanding_amount),
      color: { main: "#ed6c02", contnet: "#94a3b8", amountcolor: "#da7806" },
    },
    {
      title: "Overdue",
      subtitle: cardData?.total_overdue_invoice_count?.total != null ? new Intl.NumberFormat("en-US").format(cardData.total_overdue_invoice_count.total) : "0",
      subtitleContent: `Avg ${new Intl.NumberFormat("en-US").format(cardData?.total_overdue_invoice_count?.average_overdue_days ?? 0)} days overdue`,
      values: [],
      color: { main: "#d32f2f", contnet: "#94a3b8", amountcolor: "#d32f2f" },
    },
    {
      title: "Rental",
      subtitle: new Intl.NumberFormat("en-US").format(invoiceTypeCount?.rental ?? 0),
      subtitleContent: `${new Intl.NumberFormat("en-US").format(overdueTypeCount?.rental ?? 0)} overdue | ${cardData?.current_date && moment(cardData?.current_date).format(dateMonthYearDateFormat)}`,
      values: [],
      color: { main: "#3949ab", contnet: "#94a3b8", amountcolor: "#3949ab" },
    },
    {
      title: "Reserve",
      subtitle: new Intl.NumberFormat("en-US").format(invoiceTypeCount?.maintenance_reserve ?? 0),
      subtitleContent: `${new Intl.NumberFormat("en-US").format(overdueTypeCount?.maintenance_reserve ?? 0)} overdue | ${cardData?.current_date && moment(cardData?.current_date).format(dateMonthYearDateFormat)}`,
      values: [],
      color: { main: "#c58b00", contnet: "#94a3b8", amountcolor: "#c58b00" },
    },
    {
      title: "Credit Note",
      subtitle: new Intl.NumberFormat("en-US").format(invoiceTypeCount?.credit ?? 0),
      subtitleContent: `${new Intl.NumberFormat("en-US").format(overdueTypeCount?.credit ?? 0)} overdue | ${cardData?.current_date && moment(cardData?.current_date).format(dateMonthYearDateFormat)}`,
      values: [],
      color: { main: "#2e7d32", contnet: "#94a3b8", amountcolor: "#2e7d32" },
    },
    {
      title: "Management Fee Invoice",
      subtitle: new Intl.NumberFormat("en-US").format(invoiceTypeCount?.management_fee ?? 0),
      subtitleContent: `${new Intl.NumberFormat("en-US").format(overdueTypeCount?.management_fee ?? 0)} overdue | ${cardData?.current_date && moment(cardData?.current_date).format(dateMonthYearDateFormat)}`,
      values: [],
      color: { main: "#0891b2", contnet: "#94a3b8", amountcolor: "#0891b2" }
    },
  ];
};

const StatCard = ({ data }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const values = data?.values || [];
  const meaningfulValues = values.filter((item) => item?.amount && item.amount !== "--");
  const visibleValues = meaningfulValues.slice(0, 2);
  const hasMoreValues = meaningfulValues.length > 2;

  const handleDialogClose = () => setDialogOpen(false);
  const handleDialogOpen = () => setDialogOpen(true);

  return (
    <>
      <Card className="fleet-inv-card-datas" sx={{ width: "100%", minHeight: "90px !important", minWidth: { md: "180px", sm: "150px !important" }, maxWidth: '100%', borderTop: `3px solid ${data?.color?.main}`, borderRadius: '5px' }}>
        <CardContent className="fleet-inv-card-content">
          <Typography className="fleet-inv-card-title">{data.title}</Typography>
          {data?.subtitle !== undefined && data?.subtitle !== "" && (
            <Typography className="fleet-inv-card-sub-title" variant="h7" sx={{ color: `${data?.color?.amountcolor}` }}>
              {data.subtitle}
            </Typography>
          )}
          {visibleValues.map((item, index) => (
            <Box key={index} mb={0.5} className="card-values">
              {item.label && <Typography className="fleet-inv-card-value-label">{item.label}</Typography>}
              <Typography className="fleet-inv-card-value-amount" sx={{ color: `${data?.color?.amountcolor}` }}>
                {item.amount || "--"}
              </Typography>
            </Box>
          ))}
          {hasMoreValues && (
            <Typography onClick={handleDialogOpen} sx={{ fontSize: "11px", color: "#1976d2", cursor: "pointer", mt: 0.5, fontWeight: 500, width: "fit-content", "&:hover": { textDecoration: "none", color: `${data?.color?.amountcolor}` }, }}> Show more... </Typography>
          )}
          {data?.subtitleContent && (
            <Typography sx={{ fontSize: "10.5px", color: `${data?.color?.contnet}` }}> {data.subtitleContent} </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog fullWidth maxWidth="xs" open={dialogOpen} onClose={handleDialogClose} className="fleet-dialog">
        <DialogTitle sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: "1px solid #eef2f7", background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)", }} >
          <Box>
            <Typography className="fleet-dialog-title">{data.title}</Typography>
            {data?.subtitle && <Typography className="dialog-subtitle">{data.subtitle}</Typography>}
            {data?.subtitleContent && <Typography className="dialog-subtitle-content">{data.subtitleContent}</Typography>}
          </Box>
          <Button onClick={handleDialogClose} className="dialog-x">✕</Button>
        </DialogTitle>
        <DialogContent className="card-dialog-content">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            {meaningfulValues.map((item, index) => (
              <Box key={index} className="dialog-content-container">
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                  <Grid item xs={4}>
                    {item.label && <Typography className="card-dialog-label">{item.label}</Typography>}
                  </Grid>
                  <Grid item xs={8}>
                    <Typography className="dialog-card-amount" sx={{ color: data?.color?.amountcolor || "#0f172a" }}>
                      {item.amount || "--"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #eef2f7", backgroundColor: "#ffffff" }}>
          <Button disableRipple variant="outlined" size="small" onClick={handleDialogClose} sx={{ textTransform: "none", borderRadius: "8px", px: 2, fontWeight: 600 }}> Close </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const StatCardSkeleton = () => (
  <Card sx={{ width: "100%", minHeight: "90px !important", minWidth: { md: "180px", sm: "150px !important" }, maxWidth: "100%", borderTop: "3px solid #e0e0e0", borderRadius: "5px" }}>
    <CardContent className="fleet-inv-card-content">
      <Skeleton variant="text" width="55%" height={14} />
      <Skeleton variant="text" width="35%" height={22} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="70%" height={13} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="50%" height={11} sx={{ mt: 0.25 }} />
    </CardContent>
  </Card>
);

const FleetInvHeaderCard = forwardRef((props, ref) => {
  const [cardData, setCardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => {
    getCardDatas();
  }, []);

  const getCardDatas = () => {
    setIsLoading(true);
    globalGetService(getFleetInvCardData()).then((response) => {
      if (checkApiStatus(response)) {
        setCardData(response?.data?.data);
      }
      setIsLoading(false);
    });
  };

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 1);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scrollCards = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  useEffect(() => {
    getCardDatas();
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [cardData]);

  useImperativeHandle(ref, () => ({ refresh: getCardDatas }));
  const cards = getCards(cardData);
  return (
    <Box className="inv-cards-scroller inv-cards-scroller--fleet">
      <IconButton size="small" onClick={() => scrollCards(-1)} className={`inv-scroll-btn${showLeft ? ' inv-scroll-btn--visible' : ''}`}><ChevronLeftRoundedIcon /></IconButton>

      <Box ref={scrollRef} onScroll={updateArrows} className="inv-cards-track">
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
              <Box key={index} className="inv-card-item">
                <StatCardSkeleton />
              </Box>
            ))
          : cards.map((card, index) => (
              <Box key={index} className="inv-card-item">
                <StatCard data={card} />
              </Box>
            ))}
      </Box>

      <IconButton size="small" onClick={() => scrollCards(1)} className={`inv-scroll-btn${showRight ? ' inv-scroll-btn--visible' : ''}`}><ChevronRightRoundedIcon /></IconButton>
    </Box>
  );
});

export default FleetInvHeaderCard;